import {
    Box, Typography, alpha, useTheme,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CommentIcon from '@mui/icons-material/Comment';
import ShareIcon from '@mui/icons-material/Share';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';

const PLATFORM_ICON = {
    youtube:   YouTubeIcon,
    instagram: InstagramIcon,
    facebook:  FacebookIcon,
};

function fmtNum(n) {
    if (n == null) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
}

function MetricRow({ icon: Icon, label, value, color }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.85 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon sx={{ fontSize: 13, color: alpha(color, 0.7) }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.72rem' }}>
                    {label}
                </Typography>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.78rem', color: 'text.primary' }}>
                {value}
            </Typography>
        </Box>
    );
}

function PlatformCard({ row }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const Icon = PLATFORM_ICON[row.rawPlatform] || VideoLibraryIcon;
    const { color } = row;

    // Engagement badge colour
    const engNum = parseFloat(row.engagement);
    const engColor = engNum >= 5 ? '#10b981' : engNum >= 2 ? '#f59e0b' : '#ef4444';

    return (
        <Box
            sx={{
                position: 'relative',
                borderRadius: 4,
                overflow: 'hidden',
                border: `1.5px solid ${alpha(color, isDark ? 0.25 : 0.18)}`,
                background: isDark
                    ? `linear-gradient(145deg, ${alpha(color, 0.08)} 0%, ${alpha('#0f172a', 0.9)} 100%)`
                    : `linear-gradient(145deg, ${alpha(color, 0.05)} 0%, #fff 100%)`,
                backdropFilter: 'blur(16px)',
                transition: 'all 0.25s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 16px 40px -8px ${alpha(color, 0.3)}`,
                    borderColor: alpha(color, 0.45),
                },
            }}
        >
            {/* Ambient glow blob */}
            <Box sx={{
                position: 'absolute', top: -40, right: -40,
                width: 120, height: 120, borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(color, 0.2)} 0%, transparent 70%)`,
                pointerEvents: 'none',
            }} />

            <Box sx={{ p: 2.5 }}>
                {/* Platform header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{
                            width: 38, height: 38, borderRadius: 2.5,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(color, 0.14),
                            border: `1px solid ${alpha(color, 0.25)}`,
                        }}>
                            <Icon sx={{ fontSize: 20, color }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.2 }}>
                                {row.platform}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: alpha(color, 0.8), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                {row.posts.toLocaleString()} posts
                            </Typography>
                        </Box>
                    </Box>

                    {/* Engagement badge */}
                    <Box sx={{
                        px: 1.5, py: 0.6,
                        borderRadius: 99,
                        bgcolor: alpha(engColor, 0.12),
                        border: `1px solid ${alpha(engColor, 0.3)}`,
                        display: 'flex', alignItems: 'center', gap: 0.5,
                    }}>
                        <TrendingUpIcon sx={{ fontSize: 12, color: engColor }} />
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: engColor }}>
                            {row.engagement}%
                        </Typography>
                    </Box>
                </Box>

                {/* Divider */}
                <Box sx={{ height: '1px', bgcolor: alpha(color, 0.1), mb: 1.5 }} />

                {/* Metric rows */}
                <MetricRow icon={VisibilityIcon} label="Views"    value={fmtNum(row.views)}    color={color} />
                <MetricRow icon={ThumbUpIcon}    label="Likes"    value={fmtNum(row.likes)}    color={color} />
                <MetricRow icon={CommentIcon}    label="Comments" value={fmtNum(row.comments)} color={color} />
                {row.shares > 0 && (
                    <MetricRow icon={ShareIcon} label="Shares" value={fmtNum(row.shares)} color={color} />
                )}
            </Box>

            {/* Bottom accent bar */}
            <Box sx={{
                height: 3,
                background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)})`,
            }} />
        </Box>
    );
}

/**
 * Per-platform breakdown as Netflix-style glassmorphism cards.
 * Props: chartData — transformed platform array from OverviewTab
 */
const PlatformTable = ({ chartData }) => {
    if (!chartData?.length) return null;

    return (
        <Box>
            {/* Section header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <VideoLibraryIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
                    Platform Breakdown
                </Typography>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: `repeat(${Math.min(chartData.length, 3)}, 1fr)`,
                },
                gap: 2.5,
            }}>
                {chartData.map(row => (
                    <PlatformCard key={row.rawPlatform} row={row} />
                ))}
            </Box>
        </Box>
    );
};

export default PlatformTable;
